/**
 * The external certificate upload form (plan docs/plans/lms-plan.md 7.5,
 * 5.5 lms_external_certs; R17; WP-41): a PDF or image with title, issuer,
 * issued on, expires on, credential id, and the requirement it is offered
 * against (a course picker limited to courses whose validity is not NULL,
 * 7.5). It lands pending until an admin accepts or rejects it (Team,
 * External certificates, WP-45).
 *
 * The fields post as multipart (POST /me/external-certs, api/externalCerts.ts).
 * The checks here (required fields, the file's type and 25 MB size, expiry
 * not before issue) only spare a round trip: the server checks all of them
 * again, and the file's own bytes too (WP-34).
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Upload } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { useToast } from '../../../ui/Toast';
import { apiErrorMessage } from '../../api/client';
import { listCourses } from '../../api/courses';
import type { CourseListItem } from '../../api/courses';
import { EXTERNAL_CERT_MAX_BYTES, EXTERNAL_CERT_MIMES, uploadExternalCert } from '../../api/externalCerts';
import type { ExternalCert } from '../../api/externalCerts';

export interface ExternalCertFormProps {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: (row: ExternalCert) => void;
}

const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300';
const inputCls = `${fieldCls} mt-1 w-full`;

/** 7.5: the requirement picker offers active courses whose validity is not NULL. */
export function offerableCourses(items: CourseListItem[]): CourseListItem[] {
  return items
    .filter((c) => c.is_active !== false && c.validity_months !== null && c.validity_months !== undefined)
    .sort((a, b) => a.title.localeCompare(b.title));
}

interface FormState {
  title: string;
  issuer: string;
  issued_on: string;
  expires_on: string;
  credential_id: string;
  course_id: string;
}

const EMPTY: FormState = { title: '', issuer: '', issued_on: '', expires_on: '', credential_id: '', course_id: '' };

/** The first problem with the form, or null (the server checks everything again). */
export function validateExternalCertForm(form: FormState, file: File | null): string | null {
  if (!file) return 'Attach the certificate file (PDF, PNG or JPEG).';
  if (!(EXTERNAL_CERT_MIMES as readonly string[]).includes(file.type)) return 'The file must be a PDF, PNG or JPEG.';
  if (file.size > EXTERNAL_CERT_MAX_BYTES) return 'The file is larger than 25 MB.';
  if (!form.title.trim()) return 'Enter the certificate title.';
  if (!form.issuer.trim()) return 'Enter who issued the certificate.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.issued_on)) return 'Enter the date the certificate was issued.';
  if (form.expires_on && !/^\d{4}-\d{2}-\d{2}$/.test(form.expires_on)) return 'Enter the expiry as a date.';
  if (form.expires_on && form.expires_on < form.issued_on) return 'The expiry date is before the issue date.';
  return null;
}

export function ExternalCertForm({ isOpen, onClose, onUploaded }: ExternalCertFormProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState<CourseListItem[] | null>(null);
  const [coursesError, setCoursesError] = useState<string | null>(null);

  // A fresh form each time the dialog opens; the course list is read then.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setForm(EMPTY);
    setFile(null);
    setProblem(null);
    setCourses(null);
    setCoursesError(null);
    listCourses({ limit: 200 }).then(
      (page) => {
        if (!cancelled) setCourses(offerableCourses(Array.isArray(page?.items) ? page.items : []));
      },
      (err: unknown) => {
        if (!cancelled) {
          setCourses([]);
          setCoursesError(apiErrorMessage(err, 'The course list could not be loaded; you can still upload without linking a course.'));
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const set = (key: keyof FormState) => (e: { target: { value: string } }) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    const issue = validateExternalCertForm(form, file);
    setProblem(issue);
    if (issue || !file) return;
    setSaving(true);
    try {
      const row = await uploadExternalCert({
        file,
        title: form.title.trim(),
        issuer: form.issuer.trim(),
        issued_on: form.issued_on,
        expires_on: form.expires_on || null,
        credential_id: form.credential_id.trim() || null,
        course_id: form.course_id || null,
      });
      toast('Certificate uploaded. An admin will review it.', { type: 'success' });
      onUploaded(row);
      onClose();
    } catch (err) {
      setProblem(apiErrorMessage(err, 'The certificate could not be uploaded. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => (saving ? undefined : onClose())} title="Upload an external certificate" maxWidth="max-w-xl">
      <form onSubmit={(e) => void submit(e)} className="space-y-4" data-testid="lms-extcert-form" noValidate>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          A certificate you earned elsewhere (CITI GCP, CPR or BLS, an IATA course) is kept beside your Learning Center
          records. Once an admin accepts it, it counts toward the requirement you link it to.
        </p>
        <div>
          <label htmlFor="lms-extcert-file" className={labelCls}>
            Certificate file (PDF, PNG or JPEG, up to 25 MB)
          </label>
          <input
            id="lms-extcert-file"
            type="file"
            accept={EXTERNAL_CERT_MIMES.join(',')}
            className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:file:border-gray-600 dark:file:bg-gray-700 dark:file:text-gray-200"
            onChange={(e) => setFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null)}
            data-testid="lms-extcert-file"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="lms-extcert-title" className={labelCls}>Title</label>
            <input id="lms-extcert-title" type="text" className={inputCls} value={form.title} onChange={set('title')} maxLength={200} placeholder="CITI Good Clinical Practice" data-testid="lms-extcert-title" />
          </div>
          <div>
            <label htmlFor="lms-extcert-issuer" className={labelCls}>Issuer</label>
            <input id="lms-extcert-issuer" type="text" className={inputCls} value={form.issuer} onChange={set('issuer')} maxLength={200} placeholder="CITI Program" data-testid="lms-extcert-issuer" />
          </div>
          <div>
            <label htmlFor="lms-extcert-issued" className={labelCls}>Issued on</label>
            <input id="lms-extcert-issued" type="date" className={inputCls} value={form.issued_on} onChange={set('issued_on')} data-testid="lms-extcert-issued" />
          </div>
          <div>
            <label htmlFor="lms-extcert-expires" className={labelCls}>Expires on (optional)</label>
            <input id="lms-extcert-expires" type="date" className={inputCls} value={form.expires_on} onChange={set('expires_on')} data-testid="lms-extcert-expires" />
          </div>
          <div>
            <label htmlFor="lms-extcert-credential" className={labelCls}>Credential id (optional)</label>
            <input id="lms-extcert-credential" type="text" className={inputCls} value={form.credential_id} onChange={set('credential_id')} maxLength={200} data-testid="lms-extcert-credential" />
          </div>
          <div>
            <label htmlFor="lms-extcert-course" className={labelCls}>Counts toward (optional)</label>
            <select
              id="lms-extcert-course"
              className={inputCls}
              value={form.course_id}
              onChange={set('course_id')}
              disabled={courses === null}
              data-testid="lms-extcert-course"
            >
              <option value="">{courses === null ? 'Loading courses...' : 'Not linked to a requirement'}</option>
              {(courses ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
            {coursesError ? <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{coursesError}</p> : null}
          </div>
        </div>
        <div aria-live="polite">
          {problem ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400" data-testid="lms-extcert-problem">
              {problem}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={saving} data-testid="lms-extcert-submit">
            <Upload className="h-4 w-4" aria-hidden="true" />
            {saving ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ExternalCertForm;
